import React, { useState } from "react";
import { getThemeColors, Theme } from "../../../theme/colors";
import { getComponentColors } from "../../../theme/colors";
import axios from "axios";
import config from "../../../config";
import { ConfigDetailsData } from "./ConfigDetails";

interface SaveRdmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (savedConfig: ConfigDetailsData) => void;
  username: string;
  theme: Theme;
  configData: ConfigDetailsData | null; // The edited config data to save
}

export const SaveRdmModal: React.FC<SaveRdmModalProps> = ({
  isOpen,
  onClose,
  onSave,
  username,
  theme,
  configData,
}) => {
  const colors = getThemeColors(username, theme);
  const componentColors = getComponentColors("table", theme);

  const [configName, setConfigName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!configName.trim()) {
      setError("Config name is required");
      return;
    }

    if (!configData) {
      setError("No configuration data to save");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const saveData = {
        ...configData,
        config_name: configName.trim(),
      };

      const response = await axios.post(
        `${config.api.baseUrl}/chat/save-rdm`,
        saveData
      );

      if (response.data.success) {
        onSave(response.data.config);
        onClose();
        setConfigName("");
      } else {
        setError("Failed to save configuration");
      }
    } catch (err: unknown) {
      console.error("Error saving RDM config:", err);
      const errorMessage =
        (err as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to save configuration";
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setConfigName("");
      setError(null);
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isSaving) {
      handleSave();
    } else if (e.key === "Escape") {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: componentColors.background || colors.background,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          borderRadius: "8px",
          padding: "24px",
          minWidth: "400px",
          maxWidth: "500px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          style={{
            margin: "0 0 16px 0",
            fontSize: "20px",
            fontWeight: "bold",
            color: colors.text,
          }}
        >
          Save RDM Configuration
        </h2>

        <p
          style={{
            margin: "0 0 16px 0",
            color: colors.text,
            opacity: 0.8,
            fontSize: "14px",
          }}
        >
          Enter a name for your RDM configuration to save it.
        </p>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "600",
              color: colors.text,
            }}
          >
            Configuration Name
          </label>
          <input
            type="text"
            value={configName}
            onChange={(e) => setConfigName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter configuration name..."
            disabled={isSaving}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: `1px solid ${colors.border}`,
              borderRadius: "4px",
              backgroundColor: colors.background,
              color: colors.text,
              fontSize: "14px",
              outline: "none",
            }}
            autoFocus
          />
        </div>

        {error && (
          <div
            style={{
              marginBottom: "16px",
              padding: "8px 12px",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              border: "1px solid #f5c6cb",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={handleClose}
            disabled={isSaving}
            style={{
              padding: "8px 16px",
              backgroundColor: "transparent",
              color: colors.text,
              border: `1px solid ${colors.border}`,
              borderRadius: "4px",
              cursor: isSaving ? "not-allowed" : "pointer",
              fontSize: "14px",
              opacity: isSaving ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !configName.trim()}
            style={{
              padding: "8px 16px",
              backgroundColor:
                isSaving || !configName.trim()
                  ? colors.border
                  : colors.optionsBg,
              color: colors.text,
              border: `1px solid ${colors.border}`,
              borderRadius: "4px",
              cursor:
                isSaving || !configName.trim() ? "not-allowed" : "pointer",
              fontSize: "14px",
              opacity: isSaving || !configName.trim() ? 0.6 : 1,
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveRdmModal;
